import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getTheme, SECTION_THEMES, ROUTE_THEMES } from '../utils/themeConfig';
import { legalApi, notificationApi, securityApi, aiApi } from '../services/api';
import LegalAcceptanceModal from './LegalAcceptanceModal';
import ChangePasswordModal from './ChangePasswordModal';
import SecurityQuestionModal from './SecurityQuestionModal';
import AIChatSidebar from './AIChatSidebar';
import UniversalSearch from './UniversalSearch';
import { 
  House, Users, Briefcase, UsersThree, TreeStructure, 
  Flag, SignOut, List, X, User, CaretDown, CaretRight,
  CalendarCheck, HardHat, Package, ClipboardText, ChartBar, ListChecks,
  CurrencyDollar, Receipt, Scales, FileText, ArrowsLeftRight, ChartPie,
  FolderOpen, Stamp, ClockCounterClockwise, ChartLine, Monitor, Wrench,
  Bell, CalendarBlank, Notebook, Gear, BookOpen, Shield, ShieldCheck,
  Sun, Moon, Desktop, Warehouse, Truck, Bank, TrendUp, FilePdf, ArrowSquareOut
} from '@phosphor-icons/react';

// Each nav item has a `module` key matching the RBAC module key
const navSections = [
  {
    label: 'Planning',
    items: [
      { path: '/', label: 'Dashboard', icon: House, module: 'dashboard' },
      { path: '/users', label: 'Users', icon: Users, module: 'users' },
      { path: '/projects', label: 'Projects', icon: Briefcase, module: 'projects' },
      { path: '/teams', label: 'Teams', icon: UsersThree, module: 'teams' },
      { path: '/wbs', label: 'WBS', icon: TreeStructure, module: 'wbs' },
      { path: '/milestones-tasks', label: 'Milestones & Tasks', icon: Flag, module: 'milestones_tasks' },
      { path: '/project-planning', label: 'Project Planning', icon: ListChecks, module: 'milestones_tasks' },
    ],
  },
  {
    label: 'Execution',
    items: [
      { path: '/daily-logs', label: 'Daily Logs', icon: CalendarCheck, module: 'daily_logs' },
      { path: '/manpower', label: 'Manpower', icon: HardHat, module: 'manpower' },
      { path: '/materials', label: 'Materials', icon: Package, module: 'materials' },
      { path: '/team-reports', label: 'Team Reports', icon: ClipboardText, module: 'team_reports' },
      { path: '/productivity', label: 'Productivity', icon: ChartBar, module: 'productivity' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { path: '/budgets', label: 'Budgets', icon: CurrencyDollar, module: 'budgets' },
      { path: '/costs', label: 'Costs', icon: Receipt, module: 'costs' },
      { path: '/budget-vs-actual', label: 'Budget vs Actual', icon: Scales, module: 'budget_vs_actual' },
      { path: '/invoices', label: 'Invoices', icon: FileText, module: 'invoices' },
      { path: '/payments', label: 'Payments', icon: CurrencyDollar, module: 'payments' },
      { path: '/variations', label: 'Variations', icon: ArrowsLeftRight, module: 'variations' },
      { path: '/payroll', label: 'Payroll', icon: Users, module: 'payroll' },
      { path: '/accounting', label: 'Accounting', icon: Bank, module: 'accounting' },
      { path: '/financial-dashboard', label: 'Financial Dashboard', icon: ChartPie, module: 'financial_dashboard' },
      { path: '/vat-report', label: 'VAT Report', icon: Scales, module: 'vat_report' },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { path: '/inventory', label: 'Inventory', icon: Warehouse, module: 'inventory' },
    ],
  },
  {
    label: 'Documents',
    items: [
      { path: '/documents', label: 'Documents', icon: FolderOpen, module: 'documents' },
      { path: '/submittals', label: 'Submittals', icon: ClipboardText, module: 'documents' },
      { path: '/approvals', label: 'Approvals', icon: Stamp, module: 'approvals' },
    ],
  },
  {
    label: 'Reports',
    items: [
      { path: '/reports', label: 'Weekly Reports', icon: ChartLine, module: 'weekly_reports' },
      { path: '/advanced-reports', label: 'Advanced Reports', icon: TrendUp, module: 'advanced_reports' },
      { path: '/executive-dashboard', label: 'Executive Dashboard', icon: Monitor, module: 'executive_dashboard' },
      { path: '/project-dashboard', label: 'Project Dashboard', icon: Briefcase, module: 'project_dashboard' },
      { path: '/site-dashboard', label: 'Site Dashboard', icon: Wrench, module: 'site_dashboard' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { path: '/notifications', label: 'Notifications', icon: Bell, module: 'notifications' },
      { path: '/calendar', label: 'Calendar', icon: CalendarBlank, module: 'calendar' },
      { path: '/meetings', label: 'Meeting Notes', icon: Notebook, module: 'meetings' },
      { path: '/settings', label: 'Settings', icon: Gear, module: 'settings' },
      { path: '/tax-settings', label: 'Tax Settings', icon: Receipt, module: 'tax_settings' },
      { path: '/legal-admin', label: 'Legal & Compliance', icon: Shield, module: 'legal_admin' },
      { path: '/role-permissions', label: 'Role Permissions', icon: ShieldCheck, module: 'role_permissions' },
      { path: '/user-guide', label: 'User Guide', icon: BookOpen, module: 'user_guide' },
      { path: '/marketing-manual', label: 'Training Manual', icon: FilePdf, module: 'user_guide' },
      { path: '/audit-log', label: 'Audit Log', icon: ClockCounterClockwise, module: 'audit_log' },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout, hasPermission, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSecurityQModal, setShowSecurityQModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const branding = useBranding();
  const { preference: themePreference, isDark, setTheme } = useTheme();
  const [aiEnabled, setAiEnabled] = useState(false);

  const currentTheme = getTheme(location.pathname);
  const sectionTheme = SECTION_THEMES[currentTheme.section] || SECTION_THEMES.Planning;

  // Load AI settings
  useEffect(() => {
    aiApi.getSettings().then(r => setAiEnabled(r.data?.ai_enabled || false)).catch(() => {});
  }, [location.pathname]);

  // Check if user must change password
  useEffect(() => {
    if (user && user.must_change_password) {
      setShowPasswordModal(true);
    }
  }, [user]);

  // Check if user needs to set security question (skip demo, super admin, and must_change_password)
  useEffect(() => {
    if (!user || user.role === 'Super Admin' || user.is_demo_user || user.must_change_password) return;
    if (user.security_question_set === false) {
      setShowSecurityQModal(true);
    }
  }, [user]);

  // Check legal acceptance status (skip for Super Admin)
  useEffect(() => {
    if (!user || user.role === 'Super Admin') return;
    legalApi.getAcceptanceStatus()
      .then(res => {
        if (res.data && !res.data.accepted) setShowLegalModal(true);
      })
      .catch(() => {});
  }, [user]);

  // Fetch notification count periodically
  useEffect(() => {
    if (!user) return;
    const fetchCount = () => {
      notificationApi.unreadCount()
        .then(res => setUnreadCount(res.data?.count || 0))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const openNotifPanel = async () => {
    setNotifOpen(!notifOpen);
    setUserMenuOpen(false);
    if (!notifOpen) {
      try {
        const res = await notificationApi.list(true);
        setNotifications((res.data || []).slice(0, 8));
      } catch {}
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationApi.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/website');
  };

  // Demo read-only toast
  const [demoToast, setDemoToast] = useState(false);
  useEffect(() => {
    const handler = () => {
      setDemoToast(true);
      setTimeout(() => setDemoToast(false), 3500);
    };
    window.addEventListener('demo-readonly', handler);
    return () => window.removeEventListener('demo-readonly', handler);
  }, []);

  // Super Admin gets a dedicated nav
  const isSuperAdmin = user?.role === 'Super Admin';

  const superAdminSections = [
    {
      label: 'Super Admin',
      items: [
        { path: '/super-admin', label: 'Control Panel', icon: Shield },
      ],
    },
  ];

  const filteredSections = isSuperAdmin
    ? [...superAdminSections, ...navSections]
    : navSections.map(section => ({
        ...section,
        items: section.items.filter(item => hasPermission(item.module))
      })).filter(section => section.items.length > 0);

  const SidebarContent = () => (
    <>
      <div className="flex items-center h-16 px-4 border-b border-white/8">
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <img src="/sidebar-icon.png" alt="" className="h-9 w-9 rounded-md object-contain shrink-0" data-testid="sidebar-logo" />
          <span className="text-white font-bold text-sm leading-tight truncate">{branding.app_name}</span>
        </Link>
        <button className="lg:hidden text-white/70 hover:text-white ml-auto shrink-0" onClick={() => setSidebarOpen(false)}>
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="mt-2 px-3 pb-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
        {filteredSections.map((section) => {
          const secTheme = SECTION_THEMES[section.label] || SECTION_THEMES.Planning;
          return (
            <div key={section.label} className="mb-3">
              <div className="flex items-center gap-2 px-3 mb-1.5 mt-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: secTheme.color }} />
                <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: `${secTheme.color}CC` }}>
                  {section.label}
                </p>
              </div>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                const itemTheme = ROUTE_THEMES[item.path] || { color: secTheme.color };
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`group relative flex items-center space-x-3 px-3 py-2 rounded-lg mb-0.5 transition-all duration-200 ${
                      isActive
                        ? 'text-white'
                        : 'text-white/55 hover:text-white/90 hover:bg-white/5'
                    }`}
                    style={isActive ? { backgroundColor: `${itemTheme.color}25` } : undefined}
                    data-testid={`nav-${item.path.replace(/\//g, '') || 'dashboard'}`}
                  >
                    {/* Active left indicator */}
                    {isActive && (
                      <div
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full"
                        style={{ backgroundColor: itemTheme.color }}
                      />
                    )}
                    <item.icon
                      className="w-[18px] h-[18px] shrink-0"
                      weight={isActive ? 'fill' : 'regular'}
                      style={isActive ? { color: itemTheme.color } : undefined}
                    />
                    <span className="text-[13px] font-medium truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>
    </>
  );

  const ThemeToggle = () => {
    const opts = [
      { key: 'light', icon: Sun, label: 'Light' },
      { key: 'system', icon: Desktop, label: 'System' },
      { key: 'dark', icon: Moon, label: 'Dark' },
    ];
    return (
      <div className="flex items-center rounded-lg p-0.5" style={{ backgroundColor: 'var(--muted)' }} data-testid="theme-toggle">
        {opts.map(o => (
          <button key={o.key} onClick={() => setTheme(o.key)} title={o.label}
            className={`p-1.5 rounded-md transition-all ${themePreference === o.key ? 'shadow-sm' : 'opacity-50 hover:opacity-80'}`}
            style={themePreference === o.key ? { backgroundColor: 'var(--card-bg)', color: 'var(--text-heading)' } : { color: 'var(--text-muted)' }}
            data-testid={`theme-${o.key}`}>
            <o.icon className="w-3.5 h-3.5" weight={themePreference === o.key ? 'fill' : 'regular'} />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-60 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: 'var(--sidebar-bg)' }}>
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="lg:pl-60">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 border-b" style={{ backgroundColor: 'var(--header-bg)', borderColor: 'var(--header-border)' }}>
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <button className="lg:hidden p-2 rounded-lg" style={{ color: 'var(--text-secondary)' }} onClick={() => setSidebarOpen(true)}>
              <List className="w-5 h-5" />
            </button>

            {/* Breadcrumb */}
            <div className="hidden lg:flex items-center gap-1.5 text-sm">
              <span style={{ color: 'var(--text-muted)' }}>{currentTheme.section}</span>
              <CaretRight className="w-3 h-3" style={{ color: 'var(--border)' }} />
              <span className="font-medium" style={{ color: currentTheme.color }}>{currentTheme.label}</span>
            </div>

            <div className="flex items-center gap-2">
              <UniversalSearch aiEnabled={aiEnabled} />
              <ThemeToggle />

              {/* Notification bell */}
              <div className="relative">
                  <button
                    onClick={openNotifPanel}
                    className="relative p-2 rounded-lg transition-colors hover:bg-[var(--hover-bg)]"
                    data-testid="notification-bell"
                    title="Notifications"
                  >
                    <Bell className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} weight={unreadCount > 0 ? 'fill' : 'regular'} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-[#DC2626] text-white text-[10px] font-bold rounded-full px-1" data-testid="notif-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {notifOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setNotifOpen(false)} />
                      <div className="absolute right-0 mt-1.5 w-80 max-h-[420px] rounded-xl shadow-2xl border z-20 overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }} data-testid="notif-dropdown">
                        <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: 'var(--table-divider)' }}>
                          <p className="text-sm font-semibold" style={{ color: 'var(--text-heading)' }}>Notifications</p>
                          {unreadCount > 0 && (
                            <button onClick={handleMarkAllRead} className="text-[11px] text-[#3B82F6] hover:underline font-medium" data-testid="mark-all-read">Mark all read</button>
                          )}
                        </div>
                        <div className="overflow-y-auto max-h-[320px]">
                          {notifications.length === 0 ? (
                            <div className="px-4 py-8 text-center">
                              <Bell className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
                              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No new notifications</p>
                            </div>
                          ) : (
                            notifications.map((n, i) => (
                              <div key={i} className={`px-4 py-3 border-b text-xs transition-colors hover:bg-[var(--hover-bg)] ${!n.read ? 'bg-[#3B82F6]/5' : ''}`} style={{ borderColor: 'var(--table-divider)' }}>
                                <div className="flex items-start gap-2">
                                  {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-1.5 shrink-0" />}
                                  <div className={!n.read ? '' : 'ml-3.5'}>
                                    <p className="font-medium" style={{ color: 'var(--text-heading)' }}>{n.title || n.type}</p>
                                    <p className="mt-0.5 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{n.message}</p>
                                    <p className="mt-1" style={{ color: 'var(--text-muted)' }}>{n.created_at ? new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="px-4 py-2.5 border-t text-center" style={{ borderColor: 'var(--table-divider)' }}>
                          <button onClick={() => { setNotifOpen(false); navigate('/notifications'); }} className="text-[11px] text-[#3B82F6] hover:underline font-medium" data-testid="view-all-notif">
                            View All Notifications
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2.5 p-1.5 rounded-lg transition-colors hover:bg-[var(--hover-bg)]"
                  data-testid="user-menu-button"
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${currentTheme.color}15` }}>
                    <User className="w-4 h-4" style={{ color: currentTheme.color }} />
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-heading)' }}>{user?.name}</p>
                    <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{user?.role}</p>
                  </div>
                  <CaretDown className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-1.5 w-48 rounded-lg shadow-lg border py-1 z-20" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
                      <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--table-divider)' }}>
                        <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{user?.email}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-[#DC2626] hover:bg-[var(--danger-bg)] transition-colors"
                        data-testid="logout-button"
                      >
                        <SignOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Demo user banner */}
        {user?.is_demo_user && (
          <div className="px-4 lg:px-6 py-2 bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] flex items-center justify-between gap-3" data-testid="demo-banner">
            <p className="text-xs text-white/90">
              You are exploring <strong>ProjectBook Pro</strong> in demo mode (read-only)
            </p>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1 bg-white/15 hover:bg-white/25 text-white text-xs font-medium rounded-md transition-colors"
              data-testid="exit-demo-btn"
            >
              <ArrowSquareOut className="w-3.5 h-3.5" />
              Exit Demo
            </button>
          </div>
        )}

        {/* Demo read-only toast */}
        {demoToast && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-[#1E293B] text-white text-sm rounded-lg shadow-2xl border border-white/10 flex items-center gap-3 animate-in slide-in-from-top" data-testid="demo-readonly-toast">
            <Shield className="w-5 h-5 text-[#FBBF24] shrink-0" />
            <span>Demo mode is read-only. Sign up for full access.</span>
          </div>
        )}

        {/* Module color header bar */}
        <div
          className="px-4 lg:px-6 py-2.5 border-b flex items-center gap-3"
          style={{
            backgroundColor: `${currentTheme.color}06`,
            borderBottomColor: `${currentTheme.color}18`,
          }}
          data-testid="module-header-bar"
        >
          <div className="w-1 h-5 rounded-full" style={{ backgroundColor: currentTheme.color }} />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: `${currentTheme.color}BB` }}>
            {currentTheme.section}
          </span>
        </div>

        {/* Page content */}
        <main
          className="p-4 lg:p-6 pb-16"
          style={{ '--module-color': currentTheme.color, '--module-bg': `${currentTheme.color}08` }}
        >
          {children}
        </main>

        {/* Footer branding */}
        <footer className="px-4 lg:px-6 py-3 border-t" style={{ backgroundColor: 'var(--footer-bg)', borderColor: 'var(--header-border)' }} data-testid="app-footer">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-[12px]" style={{ color: 'var(--text-muted)' }}>
            <div className="flex items-center gap-3">
              <Link to="/legal/terms" className="hover:text-[var(--text-link)] transition-colors" data-testid="footer-terms-link">Terms of Use</Link>
              <span>&middot;</span>
              <Link to="/legal/privacy" className="hover:text-[var(--text-link)] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <span>&middot;</span>
              <Link to="/legal/disclaimer" className="hover:text-[var(--text-link)] transition-colors" data-testid="footer-disclaimer-link">Disclaimer</Link>
            </div>
            <div className="flex items-center gap-2">
              {branding.logo_url && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`} alt="ProjectBook Pro™" className="h-7 object-contain rounded logo-bg px-1" data-testid="footer-logo" />
              )}
              <span>Developed by{' '}
                <a href={branding.developer_url} target="_blank" rel="noopener noreferrer"
                   className="font-medium transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="sgk-link">
                  {branding.developer_name}
                </a>
              </span>
              {branding.developer_tagline && (
                <>
                  <span className="hidden sm:inline mx-1">&middot;</span>
                  <span className="hidden sm:inline">{branding.developer_tagline}</span>
                </>
              )}
            </div>
          </div>
        </footer>
      </div>

      {/* Legal Acceptance Modal — blocks app until accepted */}
      {showLegalModal && (
        <LegalAcceptanceModal onAccepted={() => setShowLegalModal(false)} />
      )}

      {/* Force Password Change Modal */}
      {showPasswordModal && (
        <ChangePasswordModal onSuccess={() => { setShowPasswordModal(false); checkAuth(); }} />
      )}

      {/* Force Security Question Setup Modal */}
      {showSecurityQModal && (
        <SecurityQuestionModal onComplete={() => { setShowSecurityQModal(false); checkAuth(); }} />
      )}

      {/* AI Chat Sidebar */}
      <AIChatSidebar aiEnabled={aiEnabled} />
    </div>
  );
}
